import type {PropsWithChildren} from 'react'
import {createContext, useContext, useMemo} from 'react'
import {matchPath} from '@github-ui/react-router'

import {parseCommitRange} from '../utils/parse-commit-range'
import type {CommitsSelectorCommitData} from '../page-data/payloads/file-tree'
import {ChangesRoutePath} from '../routes/route-paths'

interface SelectedRefContextData {
  startOid?: string | null
  endOid?: string | null
  baseRefOid?: string
}

export const SelectedRefContext = createContext<SelectedRefContextData>({})

export function SelectedRefContextProvider({
  baseRefOid,
  path,
  commits,
  children,
}: PropsWithChildren<{baseRefOid?: string; path: string; commits?: CommitsSelectorCommitData[]}>) {
  const value = useMemo(() => {
    const isChangesRoute = matchPath(`${ChangesRoutePath}/*`, path) !== null
    const {startOid, endOid} = isChangesRoute
      ? getStartEndOidFromPathForChanges(path, baseRefOid, commits)
      : getStartEndOidFromPath(path, baseRefOid)

    return {
      endOid,
      startOid,
      baseRefOid,
    }
  }, [baseRefOid, path, commits])

  return <SelectedRefContext.Provider value={value}>{children}</SelectedRefContext.Provider>
}

export const useSelectedRefContext = () => useContext(SelectedRefContext)

export function useHasCommitRange() {
  const {endOid, startOid} = useSelectedRefContext()
  return !!endOid || !!startOid
}

function getStartEndOidFromPath(path: string, baseRefOid?: string) {
  const pathParts = path.split('/')
  const last = pathParts[pathParts.length - 1]

  let startOid: string | undefined
  let endOid: string | undefined
  if (last) {
    const pathCommitData = parseCommitRange(last)
    if (pathCommitData) {
      startOid = 'startOid' in pathCommitData ? pathCommitData.startOid : baseRefOid
      endOid = 'endOid' in pathCommitData ? pathCommitData.endOid : pathCommitData.singleCommitOid
    }
  }

  return {startOid, endOid}
}

function getStartEndOidFromPathForChanges(path: string, baseRefOid?: string, commits?: CommitsSelectorCommitData[]) {
  const pathParts = path.split('/')
  const last = pathParts[pathParts.length - 1]

  if (!last) {
    return {startOid: undefined, endOid: undefined}
  }

  const pathCommitData = parseCommitRange(last)
  if (!pathCommitData) {
    return {startOid: undefined, endOid: undefined}
  }

  // Explicit range: startOid..endOid
  if ('startOid' in pathCommitData) {
    return {
      startOid: pathCommitData.startOid,
      endOid: pathCommitData.endOid,
    }
  }

  // Single commit: find the previous commit to use as startOid
  return getSingleCommitRange(pathCommitData.singleCommitOid, baseRefOid, commits)
}

function getSingleCommitRange(singleCommitOid: string, baseRefOid?: string, commits?: CommitsSelectorCommitData[]) {
  const endOid = singleCommitOid
  const startOid = findPreviousCommitOid(singleCommitOid, baseRefOid, commits)

  return {startOid, endOid}
}

function findPreviousCommitOid(
  commitOid: string,
  baseRefOid?: string,
  commits?: CommitsSelectorCommitData[],
): string | undefined {
  if (!commits) {
    return baseRefOid
  }

  const commitIndex = commits.findIndex(commit => commit.oid === commitOid)

  // Commit not found in list, fall back to base ref
  if (commitIndex === -1) {
    return baseRefOid
  }

  // First commit in the list, use the PR base ref
  if (commitIndex === 0) {
    return baseRefOid
  }

  // Use the previous commit as the base
  return commits[commitIndex - 1]?.oid
}

try{ SelectedRefContext.displayName ||= 'SelectedRefContext' } catch {}
try{ SelectedRefContextProvider.displayName ||= 'SelectedRefContextProvider' } catch {}